import { SerpElement } from "@nosto/search-js/preact/serp"
import type { Product } from "@/types"
import ProductImage from "./ProductImage"
import styles from "./Product.module.css"
import { cl } from "@nosto/search-js/utils"

type Props = {
  product: Product
}

function getCustomField(product: Product, key: string) {
  return product.customFields?.find(f => f.key.toLowerCase() === key)?.value
}

function hasCustomField(product: Product, key: string, value: string) {
  return getCustomField(product, key)?.toLowerCase() === value
}

function isComingSoon(product: Product) {
  return hasCustomField(product, "coming-soon", "yes")
}

/**
 * A product flagged with "coming-soon" custom field has no stock because it has not
 * been released yet, not because it sold out, so it keeps its price and skips the greyed out
 * out of stock treatment.
 */
function showOutOfStock(product: Product) {
  return product.availability === "OutOfStock" && !isComingSoon(product)
}

/**
 * Returns the "Dropping <month> <day>" label while a product's BigCommerce
 * "release time" custom field is still in the future, otherwise null.
 * The custom field is formatted like "22 may 2026 14:00:00".
 */
function getDropLabel(product: Product) {
  const releaseTime = getCustomField(product, "release time")
  if (!releaseTime) {
    return null
  }
  const releaseDate = new Date(releaseTime)
  if (isNaN(releaseDate.getTime()) || releaseDate.getTime() <= Date.now()) {
    return null
  }
  return `Dropping ${releaseDate.toLocaleDateString("en-US", { month: "long", day: "numeric" })}`
}

/**
 * Renders the badges/media/body markup for a product card. Shared by the SERP/category product
 * card and the autocomplete product card so the two stay visually identical.
 */
export function ProductCardContent({ product }: Props) {
  const isMadeInUSA = hasCustomField(product, "country of origin", "usa")
  const isExclusive = hasCustomField(product, "exclusive", "yes")
  const hasFreeShipping = product.price! > 98.99
  const dropLabel = getDropLabel(product)
  const altImage = product.alternateImageUrls?.[0]
  const outOfStock = showOutOfStock(product)
  const comingSoon = isComingSoon(product) && !dropLabel

  return (
    <>
      {(dropLabel || comingSoon || isExclusive) && (
        <div className={styles.nsBadges}>
          {dropLabel && <span className={styles.nsDropBadge}>{dropLabel}</span>}
          {comingSoon && <span className={cl(styles.nsDropBadge, styles.nsComingSoonBadge)}>Coming Soon</span>}
          {isExclusive && (
            <span className={styles.nsExclusive}>
              <img
                className={styles.nsExclusiveIcon}
                src="https://d21m4dsqdd3b9h.cloudfront.net/bigcommerce-t7tzgd0/onsiteContent/17847929364900.17662340846635938"
                alt="Exclusive"
              />
            </span>
          )}
        </div>
      )}
      <div className={styles.nsMedia}>
        <ProductImage src={product.imageUrl!} alt={product.name} className={styles.nsImage} />
        {altImage && <ProductImage src={altImage} alt={product.name} className={styles.nsImageAlt} />}
      </div>

      <div className={styles.nsBody}>
        <h3 className={styles.nsTitle}>{product.name}</h3>

        <div className={styles.nsPriceRow}>
          {outOfStock ? (
            <span className={styles.nsOutOfStock}>Out of Stock</span>
          ) : (
            <span className={styles.nsPrice}>{product.priceText}</span>
          )}
        </div>

        {(isMadeInUSA || hasFreeShipping) && (
          <div className={styles.nsFlags}>
            {isMadeInUSA && (
              <span className={cl(styles.nsFlag, styles.nsFlagUsa)}>
                <img
                  className={styles.nsFlagIcon}
                  src="https://d21m4dsqdd3b9h.cloudfront.net/bigcommerce-t7tzgd0/onsiteContent/17846206173350.6971644584557317"
                  alt="Made in USA"
                />
              </span>
            )}
            {hasFreeShipping && (
              <span className={cl(styles.nsFlag, styles.nsFlagShipping)}>
                <img
                  className={styles.nsFlagIcon}
                  src="https://d21m4dsqdd3b9h.cloudfront.net/bigcommerce-t7tzgd0/onsiteContent/17835116793570.05583324282308655"
                  alt="Free Shipping"
                />
              </span>
            )}
          </div>
        )}
      </div>
    </>
  )
}

/**
 * Renders a product search result card. Keep the SerpElement wrapper in place so Nosto search analytics can
 * track product clicks. The whole card is a single link to the product detail page.
 */
export default function Product({ product }: Props) {
  return (
    <SerpElement
      as="li"
      hit={{ productId: product.productId!, url: product.url }}
      componentProps={{ className: styles.nsProduct }}
    >
      <a
        className={cl(styles.nsCard, showOutOfStock(product) && styles.nsCardOutOfStock)}
        href={product.url}
        aria-label={product.name}
      >
        <ProductCardContent product={product} />
      </a>
    </SerpElement>
  )
}
